import { useState, useEffect } from 'react'
import { useAuth } from '@shared/auth/AuthContext'
import { useSearchParams } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  TextField,
  Button,
  Card,
  CardContent,
  Grid,
  Alert,
  Link,
  Divider,
  alpha,
  useTheme,
  CircularProgress
} from '@mui/material'
import { useForm } from 'react-hook-form'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { Business, ArrowBack } from '@mui/icons-material'
import PasswordStrengthMeter from '../../components/PasswordStrengthMeter'

// Form validation schema
const signupSchema = yup.object({
  companyName: yup
    .string()
    .required('Company name is required')
    .min(2, 'Company name must be at least 2 characters')
    .max(100, 'Company name must be less than 100 characters'),
  contactEmail: yup
    .string()
    .required('Email is required')
    .email('Please enter a valid email address')
    .max(256, 'Email must be less than 256 characters'),
  firstName: yup
    .string()
    .required('First name is required')
    .min(2, 'First name must be at least 2 characters')
    .max(50, 'First name must be less than 50 characters'),
  lastName: yup
    .string()
    .required('Last name is required')
    .min(2, 'Last name must be at least 2 characters')
    .max(50, 'Last name must be less than 50 characters'),
  password: yup
    .string()
    .required('Password is required')
    .min(15, 'Password must be at least 15 characters'),
  confirmPassword: yup
    .string()
    .required('Please confirm your password')
    .oneOf([yup.ref('password')], 'Passwords must match'),
  agreeToTerms: yup
    .boolean()
    .required('You must agree to the terms and conditions')
    .oneOf([true], 'You must agree to the terms and conditions'),
  marketingConsent: yup
    .boolean()
    .default(false) // Optional, defaults to false
})

interface SignupFormData {
  companyName: string
  contactEmail: string
  firstName: string
  lastName: string
  password: string
  confirmPassword: string
  agreeToTerms: boolean
  marketingConsent: boolean
}

export default function SignupPage() {
  const theme = useTheme()
  const { register: registerUser } = useAuth()
  const [searchParams] = useSearchParams()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [organizationSlug, setOrganizationSlug] = useState<string | undefined>(undefined)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors }
  } = useForm<SignupFormData>({
    resolver: yupResolver(signupSchema),
    mode: 'onBlur'
  })

  // Watch the password field for real-time strength checking
  const password = watch('password', '')

  // Read organization slug from URL parameter on component mount
  useEffect(() => {
    const orgParam = searchParams.get('org')
    if (orgParam) {
      setOrganizationSlug(orgParam)
    }
  }, [searchParams])

  const onSubmit = async (data: SignupFormData) => {
    setIsLoading(true)
    setError(null)

    try {
      // TODO: Handle company name - for now we register the user first
      const result = await registerUser({
        email: data.contactEmail,
        firstName: data.firstName,
        lastName: data.lastName,
        password: data.password,
        confirmPassword: data.confirmPassword,
        companyName: data.companyName,
        marketingConsent: data.marketingConsent || false,
        organizationSlug: organizationSlug
      })

      if (result.success) {
        // Store company name and email for later use in setup
        sessionStorage.setItem('signupCompanyName', data.companyName)
        sessionStorage.setItem('signupEmail', data.contactEmail)

        // Redirect to welcome/activate page (less suspicious URL pattern)
        window.location.href = '/welcome/activate'
      } else {
        setError(result.message)
      }
    } catch (err) {
      setError('Failed to create account. Please try again.')
    } finally {
      setIsLoading(false)
    }
  }

  const handleBackToHome = () => {
    window.location.href = '/'
  }

  const handleSignIn = () => {
    window.location.href = '/login'
  }


  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        py: 4
      }}
    >
      <Container maxWidth="md">
        <Box sx={{ mb: 3 }}>
          <Button
            startIcon={<ArrowBack />}
            onClick={handleBackToHome}
            sx={{
              color: 'white',
              '&:hover': {
                bgcolor: alpha('#ffffff', 0.1)
              }
            }}
          >
            Back to Home
          </Button>
        </Box>

        <Card sx={{ overflow: 'hidden' }}>
          <Grid container>
            {/* Left side - Branding */}
            <Grid
              item
              xs={12}
              md={5}
              sx={{
                background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
                color: 'white',
                p: 4,
                display: 'flex',
                flexDirection: 'column',
                justifyContent: 'center'
              }}
            >
              <Business sx={{ fontSize: 48, mb: 2 }} />
              <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                Start Your Free Trial
              </Typography>
              <Typography variant="h6" sx={{ mb: 4, opacity: 0.9 }}>
                Join thousands of teams using our helpdesk software
              </Typography>

              <Box sx={{ mb: 4 }}>
                {[
                  '14-day free trial',
                  'No credit card required',
                  'Setup in 5 minutes',
                  'Email integration included',
                  'Teams notifications',
                  'Unlimited ticket history'
                ].map((feature, index) => (
                  <Typography key={index} variant="body2" sx={{ mb: 1, opacity: 0.9 }}>
                    ✓ {feature}
                  </Typography>
                ))}
              </Box>

              <Typography variant="body2" sx={{ opacity: 0.8 }}>
                Questions? Email us at{' '}
                <Link href="mailto:support@taylindigital.com" sx={{ color: 'white' }}>
                  support@taylindigital.com
                </Link>
              </Typography>
            </Grid>

            {/* Right side - Form */}
            <Grid item xs={12} md={7}>
              <CardContent sx={{ p: 4 }}>
                <Typography variant="h4" gutterBottom sx={{ fontWeight: 'bold' }}>
                  Create Your Account
                </Typography>
                <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
                  Get started with your 14-day free trial
                </Typography>

                {error && (
                  <Alert severity="error" sx={{ mb: 3 }}>
                    {error}
                  </Alert>
                )}

                <form onSubmit={handleSubmit(onSubmit)}>
                  <Grid container spacing={3}>
                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Company Name"
                        autoComplete="organization"
                        {...register('companyName')}
                        error={!!errors.companyName}
                        helperText={errors.companyName?.message}
                        disabled={isLoading}
                        InputProps={{
                          startAdornment: <Business sx={{ color: 'action.active', mr: 1 }} />
                        }}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Business Email"
                        type="email"
                        autoComplete="email"
                        {...register('contactEmail')}
                        error={!!errors.contactEmail}
                        helperText={errors.contactEmail?.message}
                        disabled={isLoading}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="First Name"
                        autoComplete="given-name"
                        {...register('firstName')}
                        error={!!errors.firstName}
                        helperText={errors.firstName?.message}
                        disabled={isLoading}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6}>
                      <TextField
                        fullWidth
                        label="Last Name"
                        autoComplete="family-name"
                        {...register('lastName')}
                        error={!!errors.lastName}
                        helperText={errors.lastName?.message}
                        disabled={isLoading}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Password"
                        type="password"
                        autoComplete="new-password"
                        {...register('password')}
                        error={!!errors.password}
                        helperText={errors.password?.message}
                        disabled={isLoading}
                      />
                      <PasswordStrengthMeter password={password || ''} />
                    </Grid>

                    <Grid item xs={12}>
                      <TextField
                        fullWidth
                        label="Confirm Password"
                        type="password"
                        autoComplete="new-password"
                        {...register('confirmPassword')}
                        error={!!errors.confirmPassword}
                        helperText={errors.confirmPassword?.message}
                        disabled={isLoading}
                      />
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <input
                          type="checkbox"
                          {...register('agreeToTerms')}
                          disabled={isLoading}
                          style={{ marginTop: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          I agree to the{' '}
                          <Link href="/terms" target="_blank">
                            Terms of Service
                          </Link>{' '}
                          and{' '}
                          <Link href="/privacy" target="_blank">
                            Privacy Policy
                          </Link>
                        </Typography>
                      </Box>
                      {errors.agreeToTerms && (
                        <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>
                          {errors.agreeToTerms.message}
                        </Typography>
                      )}
                    </Grid>

                    <Grid item xs={12}>
                      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                        <input
                          type="checkbox"
                          {...register('marketingConsent')}
                          disabled={isLoading}
                          style={{ marginTop: 2 }}
                        />
                        <Typography variant="body2" color="text.secondary">
                          I would like to receive product updates, tips, and special offers via email (optional)
                        </Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <Button
                        type="submit"
                        fullWidth
                        variant="contained"
                        size="large"
                        disabled={isLoading}
                        sx={{
                          py: 1.5,
                          fontSize: '1.1rem',
                          fontWeight: 'bold'
                        }}
                      >
                        {isLoading ? (
                          <>
                            <CircularProgress size={20} sx={{ mr: 1 }} />
                            Creating Account...
                          </>
                        ) : (
                          'Start Free Trial'
                        )}
                      </Button>
                    </Grid>
                  </Grid>
                </form>

                <Divider sx={{ my: 3 }} />

                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mb: 2 }}>
                  Already have an account?{' '}
                  <Link
                    component="button"
                    type="button"
                    onClick={handleSignIn}
                    sx={{ fontWeight: 'bold' }}
                  >
                    Sign In
                  </Link>
                </Typography>

                <Typography variant="body2" color="text.secondary" textAlign="center" sx={{ mt: 3, pt: 2, borderTop: '1px solid', borderColor: 'divider' }}>
                  <Link
                    href="https://github.com/taylin-studio/helpdesk/blob/main/docs/USER-GUIDE-CUSTOMER.md"
                    target="_blank"
                    rel="noopener noreferrer"
                    sx={{ textDecoration: 'none', fontWeight: 'medium' }}
                  >
                    📖 Need help getting started? View Customer Guide
                  </Link>
                </Typography>
              </CardContent>
            </Grid>
          </Grid>
        </Card>
      </Container>
    </Box>
  )
}